import React, { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import {
  generateGoogleCalendarUrl,
  generateAppleCalendarUrl,
  generateOutlookCalendarUrl,
  downloadICSFile,
} from "../../../utils/calendarUtils";

const CalendarDropdown = ({
  title,
  description,
  location,
  start,
  end,
  buttonStyle = {},
  isMobile = false,
  iconSize = 16,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (!start || !end) {
    return null;
  }

  const handleGoogleCalendar = () => {
    const url = generateGoogleCalendarUrl({ title, description, location, start, end });
    window.open(url, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const handleAppleCalendar = () => {
    // Apple Calendar downloads .ics file directly
    generateAppleCalendarUrl({ title, description, location, start, end });
    setIsOpen(false);
  };

  const handleOutlookCalendar = () => {
    const url = generateOutlookCalendarUrl({ title, description, location, start, end });
    window.open(url, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const handleDownloadICS = () => {
    downloadICSFile({ title, description, location, start, end });
    setIsOpen(false);
  };

  // Determine if this is a light/white button style (for agenda items)
  const bg = buttonStyle.backgroundColor || "";
  const normalizedBg = typeof bg === "string" ? bg.toLowerCase() : "";
  const isLightButton =
    normalizedBg === "#ffffff" ||
    normalizedBg === "#fff" ||
    normalizedBg.startsWith("rgb(255, 255, 255)") ||
    normalizedBg.includes("255, 255, 255");

  // Icon color: dark icon on light button, light icon on dark button
  const iconColor = isLightButton ? "#000000" : "#ffffff";

  // Default \"Add to calendar\" pill button style
  const defaultButtonStyle = {
    padding: isMobile ? "0.6rem 1.3rem" : "0.65rem 1.5rem",
    fontSize: isMobile ? "0.9rem" : "0.95rem",
    fontWeight: 600,
    backgroundColor: isLightButton ? "#ffffff" : "rgba(255, 255, 255, 0.09)",
    color: isLightButton ? "#374151" : "#ffffff",
    border: isLightButton ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.35)",
    borderRadius: "999px",
    cursor: "pointer",
    backdropFilter: isLightButton ? "none" : "blur(12px)",
    boxShadow: isLightButton ? "0 2px 4px rgba(0,0,0,0.08)" : "0 10px 30px rgba(15,23,42,0.5)",
    transition: "background-color 0.2s, transform 0.2s, box-shadow 0.2s, border-color 0.2s",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.4rem",
    minWidth: isMobile ? "auto" : "180px",
    position: "relative",
    ...buttonStyle,
  };

  // Hover handlers based on button style
  const handleMouseOver = (e) => {
    if (isLightButton) {
      e.currentTarget.style.backgroundColor = "#f9fafb";
      e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.08)";
      e.currentTarget.style.transform = "translateY(-1px)";
    } else {
      // For colored buttons (like the orange agenda button), keep the same
      // background and just enhance elevation/transform so it doesn't flash white.
      e.currentTarget.style.backgroundColor =
        buttonStyle.backgroundColor || defaultButtonStyle.backgroundColor;
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 16px 32px rgba(15,23,42,0.35)";
      e.currentTarget.style.borderColor =
        buttonStyle.border || defaultButtonStyle.border;
    }
  };

  const handleMouseOut = (e) => {
    if (isLightButton) {
      e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor || "#ffffff";
      e.currentTarget.style.boxShadow = buttonStyle.boxShadow || "0 2px 4px rgba(0,0,0,0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    } else {
      // Restore original styles for colored buttons
      e.currentTarget.style.backgroundColor =
        buttonStyle.backgroundColor || defaultButtonStyle.backgroundColor;
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow =
        buttonStyle.boxShadow || "0 10px 30px rgba(15,23,42,0.5)";
      e.currentTarget.style.borderColor =
        buttonStyle.border || defaultButtonStyle.border;
    }
  };

  const dropdownMenuStyle = {
    position: "absolute",
    top: "100%",
    left: 0, // Align dropdown to the left edge of the icon button
    marginTop: "8px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    border: "1px solid #e5e7eb",
    minWidth: "220px",
    zIndex: 1000,
    overflow: "hidden",
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? "visible" : "hidden",
    transform: isOpen ? "translateY(0)" : "translateY(-10px)",
    transition: "all 0.2s ease",
  };

  const menuItemStyle = {
    padding: "12px 16px",
    fontSize: "0.9rem",
    color: "#374151",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "background-color 0.15s",
    borderBottom: "1px solid #f3f4f6",
  };

  const menuItemHoverStyle = {
    backgroundColor: "#f9fafb",
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={defaultButtonStyle}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        aria-label="Add to calendar"
        title="Add to calendar"
      >
        <Calendar
          size={iconSize || 16}
          color={iconColor}
        />
        <span>Add to calendar</span>
        <ChevronDown size={14} />
      </button>

      <div style={dropdownMenuStyle}>
        <div
          style={menuItemStyle}
          onClick={handleGoogleCalendar}
          onMouseEnter={(e) => {
            Object.assign(e.currentTarget.style, menuItemHoverStyle);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          <span>Google Calendar</span>
        </div>

        <div
          style={menuItemStyle}
          onClick={handleAppleCalendar}
          onMouseEnter={(e) => {
            Object.assign(e.currentTarget.style, menuItemHoverStyle);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#000000">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          <span>Apple Calendar</span>
        </div>

        <div
          style={menuItemStyle}
          onClick={handleOutlookCalendar}
          onMouseEnter={(e) => {
            Object.assign(e.currentTarget.style, menuItemHoverStyle);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#0078D4">
            <path d="M7.5 7.5h9v9h-9v-9zm1.5 1.5v6h6V9H9zm10.5-1.5h-3V4.5h-1.5V7.5H9V4.5H7.5V7.5H4.5v1.5H21V7.5zm0 12h-3v3h-1.5v-3H9v3H7.5v-3H4.5v-1.5H21v1.5z" />
          </svg>
          <span>Outlook</span>
        </div>

        <div
          style={{ ...menuItemStyle, borderBottom: "none" }}
          onClick={handleDownloadICS}
          onMouseEnter={(e) => {
            Object.assign(e.currentTarget.style, menuItemHoverStyle);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>Download .ics file</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarDropdown;
